import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Trash2, Gift, Users, Calendar, CheckCircle, XCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, btn } from "@/components/Page";
import Modal from "@/components/Modal";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { useAuth } from "@/context/AuthContext";

export default function PromoCodesAdmin() {
  const { user } = useAuth();
  const [codes, setCodes] = useState(null);
  const [createModal, setCreateModal] = useState(false);
  const [viewModal, setViewModal] = useState(null); // {code, redemptions}
  const [busy, setBusy] = useState(false);
  
  // Create form state
  const [newCode, setNewCode] = useState("");
  const [goldReward, setGoldReward] = useState(0);
  const [follisReward, setFollisReward] = useState(0);
  const [consumables, setConsumables] = useState([{ id: "", count: 1 }]);

  const loadCodes = async () => {
    try {
      const { data } = await api.get("/promo-codes/admin/list");
      setCodes(data.promo_codes);
    } catch (e) {
      toast.error(apiErr(e));
      setCodes([]);
    }
  };

  useEffect(() => {
    loadCodes();
  }, []);

  const createCode = async (e) => {
    e.preventDefault();
    
    if (!newCode.trim()) {
      toast.error("Please enter a promo code");
      return;
    }
    
    // Validate rewards
    const validConsumables = consumables.filter(c => c.id && c.count > 0);
    if (goldReward === 0 && follisReward === 0 && validConsumables.length === 0) {
      toast.error("Please add at least one reward");
      return;
    }
    
    setBusy(true);
    try {
      await api.post("/promo-codes/admin/create", {
        code: newCode.trim(),
        rewards: {
          gold: goldReward,
          follis: follisReward,
          consumables: validConsumables
        }
      });
      
      toast.success(`Promo code '${newCode.toUpperCase()}' created!`);
      setNewCode("");
      setGoldReward(0);
      setFollisReward(0);
      setConsumables([{ id: "", count: 1 }]);
      setCreateModal(false);
      loadCodes();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (code, currentlyActive) => {
    setBusy(true);
    try {
      const endpoint = currentlyActive ? "deactivate" : "activate";
      await api.put(`/promo-codes/admin/${code}/${endpoint}`);
      toast.success(`Code ${currentlyActive ? "deactivated" : "activated"}`);
      loadCodes();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const viewRedemptions = async (code) => {
    try {
      const { data } = await api.get(`/promo-codes/admin/${code}/redemptions`);
      setViewModal({ code, redemptions: data.redemptions, total: data.total });
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const addConsumable = () => {
    setConsumables([...consumables, { id: "", count: 1 }]);
  };

  const updateConsumable = (index, field, value) => {
    const updated = [...consumables];
    updated[index][field] = value;
    setConsumables(updated);
  };

  const removeConsumable = (index) => {
    setConsumables(consumables.filter((_, i) => i !== index));
  };

  if (codes === null) return <Loader label="Loading promo codes…" />;
  
  if (user && !user.is_admin) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6 pb-10">
      <PageHeader 
        title="Promo Codes" 
        subtitle="Create and manage promotional codes for players"
        icon={Gift}
      />

      <div className="flex justify-end">
        <button
          onClick={() => setCreateModal(true)}
          className={btn() + " flex items-center gap-2"}
        >
          <Plus size={16} /> Create Promo Code
        </button>
      </div>

      {/* Codes Table */}
      <div className="border border-amber-900/30 bg-zinc-950/60">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-amber-900/30">
              <tr className="text-left text-xs uppercase tracking-wide text-zinc-400">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Rewards</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Redemptions</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {codes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-zinc-600">
                    No promo codes yet. Create one to get started.
                  </td>
                </tr>
              ) : (
                codes.map((code) => (
                  <tr key={code.code} className="hover:bg-zinc-900/40 transition-colors">
                    <td className="px-4 py-3">
                      <span className="font-mono text-amber-300 font-semibold">{code.code}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-300">
                      {code.rewards.gold > 0 && (
                        <div className="flex items-center gap-1">
                          <GoldCoin size={14} />
                          <span>{code.rewards.gold}</span>
                        </div>
                      )}
                      {code.rewards.follis > 0 && (
                        <div className="flex items-center gap-1">
                          <FollisCoin size={14} />
                          <span>{code.rewards.follis}</span>
                        </div>
                      )}
                      {code.rewards.consumables.map((c, i) => (
                        <div key={i} className="text-xs text-zinc-400">
                          {c.count}x {c.id.replace(/_/g, " ")}
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-3">
                      {code.active ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-400">
                          <CheckCircle size={14} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-zinc-500">
                          <XCircle size={14} /> Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => viewRedemptions(code.code)}
                        className="text-amber-400 hover:text-amber-300 font-semibold"
                      >
                        {code.redemption_count}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">
                      {new Date(code.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => viewRedemptions(code.code)}
                          className="text-zinc-400 hover:text-amber-400"
                          title="View redemptions"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => toggleActive(code.code, code.active)}
                          disabled={busy}
                          className={`text-xs px-3 py-1 rounded ${
                            code.active
                              ? "bg-zinc-800 hover:bg-zinc-700 text-zinc-300"
                              : "bg-emerald-600 hover:bg-emerald-500 text-white"
                          }`}
                        >
                          {code.active ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Modal */}
      {createModal && (
        <Modal open={createModal} onClose={() => setCreateModal(false)} title="Create Promo Code">
          <form onSubmit={createCode} className="space-y-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-1">Promo Code</label>
              <input
                type="text"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                placeholder="e.g., SUMMER2026"
                maxLength={30}
                className="w-full bg-zinc-900 border border-amber-900/40 px-3 py-2 text-zinc-100 font-mono focus:border-amber-600 outline-none"
              />
              <p className="text-xs text-zinc-600 mt-1">3-30 characters, case-insensitive</p>
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-1">Gold Reward</label>
              <input
                type="number"
                data-testid="promo-code-gold-reward-input"
                value={goldReward}
                onChange={(e) => setGoldReward(Math.max(0, parseInt(e.target.value) || 0))}
                min={0}
                className="w-full bg-zinc-900 border border-amber-900/40 px-3 py-2 text-zinc-100 focus:border-amber-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-1">Follis Reward</label>
              <input
                type="number"
                data-testid="promo-code-follis-reward-input"
                value={follisReward}
                onChange={(e) => setFollisReward(Math.max(0, parseInt(e.target.value) || 0))}
                min={0}
                className="w-full bg-zinc-900 border border-amber-900/40 px-3 py-2 text-zinc-100 focus:border-amber-600 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-2">Consumable Rewards</label>
              {consumables.map((c, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <select
                    value={c.id}
                    onChange={(e) => updateConsumable(i, "id", e.target.value)}
                    className="flex-1 bg-zinc-900 border border-amber-900/40 px-3 py-2 text-zinc-100 focus:border-amber-600 outline-none"
                  >
                    <option value="">Select item...</option>
                    <option value="common_weapon_fragment">Common Weapon Fragment</option>
                    <option value="common_armor_fragment">Common Armor Fragment</option>
                    <option value="uncommon_weapon_fragment">Uncommon Weapon Fragment</option>
                    <option value="uncommon_armor_fragment">Uncommon Armor Fragment</option>
                    <option value="rare_weapon_fragment">Rare Weapon Fragment</option>
                    <option value="rare_armor_fragment">Rare Armor Fragment</option>
                    <option value="elite_weapon_fragment">Elite Weapon Fragment</option>
                    <option value="elite_armor_fragment">Elite Armor Fragment</option>
                    <option value="legendary_weapon_fragment">Legendary Weapon Fragment</option>
                    <option value="legendary_armor_fragment">Legendary Armor Fragment</option>
                    <option value="beast_food">Beast Food</option>
                    <option value="premium_beast_food">Premium Beast Food</option>
                    <option value="breeding_ticket">Breeding Ticket</option>
                  </select>
                  <input
                    type="number"
                    value={c.count}
                    onChange={(e) => updateConsumable(i, "count", Math.max(1, parseInt(e.target.value) || 1))}
                    min={1}
                    className="w-20 bg-zinc-900 border border-amber-900/40 px-3 py-2 text-zinc-100 focus:border-amber-600 outline-none"
                  />
                  {consumables.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeConsumable(i)}
                      className="text-red-400 hover:text-red-300 px-2"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addConsumable}
                className={btn("ghost") + " text-xs w-full mt-2"}
              >
                <Plus size={14} /> Add Item
              </button>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                type="button"
                onClick={() => setCreateModal(false)}
                className={btn("ghost")}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className={btn()}
              >
                Create Code
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* View Redemptions Modal */}
      {viewModal && (
        <Modal 
          open={!!viewModal} 
          onClose={() => setViewModal(null)} 
          title={`Redemptions: ${viewModal.code}`}
        >
          <div className="space-y-3">
            <p className="text-sm text-zinc-400">
              <span className="text-amber-300 font-semibold">{viewModal.total}</span> redemptions
            </p>
            
            {viewModal.redemptions.length === 0 ? (
              <p className="text-center text-zinc-600 py-6">No redemptions yet</p>
            ) : (
              <div className="max-h-96 overflow-y-auto space-y-2">
                {viewModal.redemptions.map((r, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between bg-zinc-900 border border-zinc-800 px-3 py-2"
                  >
                    <div>
                      <p className="text-zinc-200 font-cinzel">{r.user_name}</p>
                      <p className="text-xs text-zinc-600">{r.user_id}</p>
                    </div>
                    <div className="text-right text-xs text-zinc-500">
                      {new Date(r.redeemed_at).toLocaleDateString()}<br />
                      {new Date(r.redeemed_at).toLocaleTimeString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
